import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertBox,
  Button,
  Input,
  InputGroup,
  ModalInfo,
  ModalInfoProps,
  Support,
} from '@/components';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { promoHistorySelectors, userSelectors } from '@/redux/selectors';
import { blockActions, promoHistoryActions } from '@/redux/actions';
import { IssuePromoCodeRequest } from '@/api';
import { useInputText } from '@/hooks';
import parse from 'html-react-parser';
import { Validation } from '@/models';

import styles from './ShareBalance.module.sass';

interface ShareBalanceProps extends ModalInfoProps {}

const ShareBalance: FC<ShareBalanceProps> = (props) => {
  const { isOpen, onClose } = props;
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { loading, error } = useAppSelector(
    promoHistorySelectors.getPromoHistorySlice,
  );
  const { data } = useAppSelector(userSelectors.getUserSlice);

  const AMOUNT_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
      {
        name: 'regex',
        value: /^\d+$/i,
        message: t('translation.field_must_contain_only_digits'),
      },
      {
        name: 'gt',
        value: 0,
        message: t('translation.field_must_be_greater', { value: 0 }),
      },
      {
        name: 'lt',
        value: data?.extBalance || 0,
        message: t('translation.user_promo_modal_filed_validation'),
      },
    ],
    [t],
  );

  const amountField = useInputText('', AMOUNT_VALIDATIONS);

  useEffect(() => {
    if (isOpen) {
      amountField.onReset();
      dispatch(blockActions.block());
    } else {
      dispatch(blockActions.unblock());
    }
    dispatch(promoHistoryActions.setError(null));
  }, [isOpen]);

  const submitHandler = () => {
    if (!loading) {
      if (amountField.isValid) {
        const payload: IssuePromoCodeRequest = {
          amount: parseInt(amountField.value, 10),
        };

        dispatch(promoHistoryActions.issuePromoCode(payload));
      } else if (!amountField.isValid) {
        amountField.onBlur();
      }
    }
  };

  const amountLabel = t('translation.issue_promo_value_msg');

  return (
    <ModalInfo
      isOpen={isOpen}
      title={t('translation.user_promo_modal_title')}
      className={styles.modal}
      actions={
        <>
          <Button
            type="primary"
            corner="rect"
            onClick={submitHandler}
            disabled={loading}
            isLoading={loading}
          >
            {t('translation.issue_promo_btn')}
          </Button>
          <Button
            type="secondary"
            corner="rect"
            onClick={onClose}
            disabled={loading}
          >
            {t('translation.cancel_btn')}
          </Button>
        </>
      }
      onClose={onClose}
    >
      <div className={styles.form}>
        <InputGroup label={amountLabel} className={styles.inputGroup}>
          <Input
            id="amount"
            name="amount"
            type="text"
            value={amountField.value}
            onChange={amountField.onChange}
            onBlur={amountField.onBlur}
            disabled={loading}
            placeholder=""
            isError={amountField.isDirty && !amountField.isValid}
            required
          />
          {amountField.isDirty && !amountField.isValid && (
            <AlertBox type="danger" small>
              {{
                description: amountField.errors[0],
              }}
            </AlertBox>
          )}
          {!!error && (
            <>
              <AlertBox type="danger" className={styles.alert}>
                {{
                  title: parse(t('translation.error')),
                  description: error,
                  icon: '🤚',
                }}
              </AlertBox>

              <AlertBox type="info" className={styles.info}>
                {{
                  title: t('translation.tips'),
                  description: (
                    <div className={styles.tips}>
                      <ul className={styles.list}>
                        <li>
                          <div className={styles.wrapper}>
                            <span className={styles.gap}>
                              {t('translation.contact_support')}
                            </span>
                            <Support />
                          </div>
                        </li>
                      </ul>
                    </div>
                  ),
                }}
              </AlertBox>
            </>
          )}
        </InputGroup>
      </div>
    </ModalInfo>
  );
};

export default ShareBalance;
